/* ==========================================================================
   Box It — letter maker + grid background (assets/box-it-letters.js)
   Add-on for assets/box-it.js.

   1. Letter maker: type one character, tap a bold font, and the glyph is
      turned into a clean SVG (outline + counters as holes) and fed into Box It
      through its normal upload input, so everything downstream (hole walls,
      picker, tracking, download) works exactly as for an uploaded SVG.
      window.BX_LETTER is true while that change event runs, so box-it.js records
      input_source 'letter' and skips bx_file_upload (bx_letter_select is sent here).
   2. Grid: the preview's white sheet is replaced by a true-scale cutting-mat
      grid (1 in squares in inches, 10 mm squares in mm), PerfectScore style,
      and every piece is filled white so it reads as paper (preview only).

   Fonts: open-licence Google Fonts via Fontsource on jsDelivr (.woff, CORS ok).
   Glyph outlines: opentype.js from cdnjs, loaded on first use.
   ========================================================================== */
(function () {
  'use strict';

  var FONTS = [
    { id: 'archivo-black', name: 'Archivo Black', w: 400 },
    { id: 'anton', name: 'Anton', w: 400 },
    { id: 'bebas-neue', name: 'Bebas Neue', w: 400 },
    { id: 'titan-one', name: 'Titan One', w: 400 },
    { id: 'bungee', name: 'Bungee', w: 400 },
    { id: 'alfa-slab-one', name: 'Alfa Slab One', w: 400 },
    { id: 'ultra', name: 'Ultra', w: 400 },
    { id: 'luckiest-guy', name: 'Luckiest Guy', w: 400 },
    { id: 'lilita-one', name: 'Lilita One', w: 400 },
    { id: 'passion-one', name: 'Passion One', w: 700 },
    { id: 'changa-one', name: 'Changa One', w: 400 },
    { id: 'bowlby-one-sc', name: 'Bowlby One', w: 400 },
    { id: 'rammetto-one', name: 'Rammetto One', w: 400 },
    { id: 'sigmar-one', name: 'Sigmar One', w: 400 },
    { id: 'concert-one', name: 'Concert One', w: 400 }
  ];
  var OPENTYPE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/opentype.js/1.3.4/opentype.min.js';
  var MAX_PTS = 96;        // Box It skips loops over 120 points; stay well under
  var GLYPH_SIZE = 1000;   // font units → SVG units (Box It rescales to the chosen width)

  function fontUrl(f) {
    return 'https://cdn.jsdelivr.net/npm/@fontsource/' + f.id + '/files/' + f.id + '-latin-' + f.w + '-normal.woff';
  }
  function $(id) { return document.getElementById(id); }
  function track(name, props) {
    try { if (window.BT && typeof window.BT.track === 'function') window.BT.track(name, props); } catch (e) {}
  }

  /* ---------------- glyph → polygons ---------------- */
  function flatten(cmds) {
    var loops = [], cur = [], p = [0, 0], i, t, n = 10;
    for (i = 0; i < cmds.length; i++) {
      var c = cmds[i];
      if (c.type === 'M') { if (cur.length > 2) loops.push(cur); cur = [[c.x, c.y]]; p = [c.x, c.y]; }
      else if (c.type === 'L') { cur.push([c.x, c.y]); p = [c.x, c.y]; }
      else if (c.type === 'Q') {
        for (t = 1; t <= n; t++) {
          var u = t / n, a = (1 - u) * (1 - u), b = 2 * (1 - u) * u, d = u * u;
          cur.push([a * p[0] + b * c.x1 + d * c.x, a * p[1] + b * c.y1 + d * c.y]);
        }
        p = [c.x, c.y];
      } else if (c.type === 'C') {
        for (t = 1; t <= n; t++) {
          var v = t / n, a3 = Math.pow(1 - v, 3), b3 = 3 * Math.pow(1 - v, 2) * v, c3 = 3 * (1 - v) * v * v, d3 = v * v * v;
          cur.push([a3 * p[0] + b3 * c.x1 + c3 * c.x2 + d3 * c.x, a3 * p[1] + b3 * c.y1 + c3 * c.y2 + d3 * c.y]);
        }
        p = [c.x, c.y];
      } else if (c.type === 'Z') { if (cur.length > 2) loops.push(cur); cur = []; }
    }
    if (cur.length > 2) loops.push(cur);
    return loops.map(function (l) {
      var out = [l[0]];
      for (var k = 1; k < l.length; k++) {
        var q = out[out.length - 1];
        if (Math.hypot(l[k][0] - q[0], l[k][1] - q[1]) > 1e-6) out.push(l[k]);
      }
      var f = out[0], z = out[out.length - 1];
      if (out.length > 1 && Math.hypot(f[0] - z[0], f[1] - z[1]) < 1e-6) out.pop();
      return out;
    }).filter(function (l) { return l.length > 2; });
  }

  function rdp(pts, eps) {
    if (pts.length < 3) return pts.slice();
    var keep = new Uint8Array(pts.length), stack = [[0, pts.length - 1]];
    keep[0] = keep[pts.length - 1] = 1;
    while (stack.length) {
      var r = stack.pop(), s = r[0], e = r[1], A = pts[s], B = pts[e];
      var dx = B[0] - A[0], dy = B[1] - A[1], L = Math.hypot(dx, dy) || 1e-9, best = -1, bi = -1;
      for (var i = s + 1; i < e; i++) {
        var dist = Math.abs(dy * pts[i][0] - dx * pts[i][1] + B[0] * A[1] - B[1] * A[0]) / L;
        if (dist > best) { best = dist; bi = i; }
      }
      if (best > eps) { keep[bi] = 1; stack.push([s, bi], [bi, e]); }
    }
    return pts.filter(function (_, i) { return keep[i]; });
  }

  // Closed-loop simplify: split at the point farthest from the start so both halves are open runs
  function simplifyLoop(loop, eps) {
    var far = 0, fd = -1;
    for (var i = 1; i < loop.length; i++) {
      var d = Math.hypot(loop[i][0] - loop[0][0], loop[i][1] - loop[0][1]);
      if (d > fd) { fd = d; far = i; }
    }
    var a = rdp(loop.slice(0, far + 1), eps), b = rdp(loop.slice(far).concat([loop[0]]), eps);
    return a.concat(b.slice(1, -1));
  }

  function glyphSVG(font, ch) {
    var path = font.getPath(ch, 0, 0, GLYPH_SIZE);
    var loops = flatten(path.commands);
    if (!loops.length) return null;
    var x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    loops.forEach(function (l) { l.forEach(function (p) {
      if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0];
      if (p[1] < y0) y0 = p[1]; if (p[1] > y1) y1 = p[1];
    }); });
    var w = x1 - x0, h = y1 - y0, size = Math.max(w, h);
    var simple = loops.map(function (l) {
      var eps = size * 0.0025, s = simplifyLoop(l, eps);
      while (s.length > MAX_PTS && eps < size * 0.05) { eps *= 1.35; s = simplifyLoop(l, eps); }
      return s;
    }).filter(function (l) { return l.length > 2; });
    // pad so a rectangular glyph (I, l, 1 in some fonts) is never mistaken for a background canvas
    var pad = size * 0.08, f = function (n) { return Math.round(n * 100) / 100; };
    var d = simple.map(function (l) {
      return 'M' + l.map(function (p) { return f(p[0] - x0 + pad) + ' ' + f(p[1] - y0 + pad); }).join(' L') + ' Z';
    }).join(' ');
    var W = f(w + 2 * pad), H = f(h + 2 * pad);
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + H + '" width="' + W + '" height="' + H + '">' +
      '<path fill="#000" fill-rule="evenodd" d="' + d + '"/></svg>';
  }

  /* ---------------- font loading ---------------- */
  var otPromise = null, parsed = {}, faces = {};
  function loadOpentype() {
    if (window.opentype) return Promise.resolve(window.opentype);
    if (otPromise) return otPromise;
    otPromise = new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = OPENTYPE_URL; s.async = true;
      s.onload = function () { window.opentype ? res(window.opentype) : rej(new Error('opentype')); };
      s.onerror = function () { otPromise = null; rej(new Error('opentype')); };
      document.head.appendChild(s);
    });
    return otPromise;
  }
  function loadFont(f) {
    if (parsed[f.id]) return parsed[f.id];
    parsed[f.id] = Promise.all([
      loadOpentype(),
      fetch(fontUrl(f)).then(function (r) { if (!r.ok) throw new Error('font'); return r.arrayBuffer(); })
    ]).then(function (res) { return res[0].parse(res[1]); })
      .catch(function (e) { delete parsed[f.id]; throw e; });
    return parsed[f.id];
  }
  // display face for the chip previews (same file, so the browser cache serves the outline fetch)
  function loadFace(f) {
    if (faces[f.id] || !('FontFace' in window)) return;
    faces[f.id] = 1;
    try {
      var face = new FontFace('bxl-' + f.id, 'url(' + fontUrl(f) + ')', { display: 'swap' });
      face.load().then(function (ff) { document.fonts.add(ff); }).catch(function () {});
    } catch (e) {}
  }

  /* ---------------- letter step UI ---------------- */
  var state = { font: null, busy: 0 };

  function currentChar() {
    var v = ($('bxl-char').value || '').trim();
    return v ? Array.from(v)[0] : '';
  }
  function msg(text, isErr) {
    var m = $('bxl-msg');
    m.textContent = text || '';
    m.classList.toggle('is-err', !!isErr);
  }
  function renderChips() {
    var ch = currentChar() || 'B', wrap = $('bxl-fonts');
    wrap.querySelectorAll('.bxl-chip__glyph').forEach(function (g) { g.textContent = ch; });
  }
  function buildChips() {
    var wrap = $('bxl-fonts');
    wrap.innerHTML = '';
    FONTS.forEach(function (f) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'bxl-chip';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', 'false');
      b.dataset.font = f.id;
      b.title = f.name;
      b.innerHTML = '<span class="bxl-chip__glyph" style="font-family:\'bxl-' + f.id + '\',sans-serif"></span>' +
        '<span class="bxl-chip__name">' + f.name + '</span>';
      b.addEventListener('click', function () { pick(f); });
      wrap.appendChild(b);
    });
    renderChips();
  }
  function markChip(id) {
    $('bxl-fonts').querySelectorAll('.bxl-chip').forEach(function (b) {
      b.setAttribute('aria-checked', b.dataset.font === id ? 'true' : 'false');
    });
  }

  function feed(svgText, name) {
    var input = $('bx-file');
    var file = new File([svgText], name, { type: 'image/svg+xml' });
    try {
      var dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      window.BX_LETTER = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    } catch (e) {
      return false;
    } finally {
      window.BX_LETTER = false;
    }
  }

  function apply() {
    var f = state.font, ch = currentChar();
    if (!f) return;
    if (!ch) { msg('Type a letter or number first.', true); return; }
    var ticket = ++state.busy;
    msg('Loading ' + f.name + '…');
    loadFont(f).then(function (font) {
      if (ticket !== state.busy) return;
      var glyph = font.charToGlyph(ch);
      if (!glyph || glyph.index === 0) { msg(f.name + ' doesn’t have “' + ch + '”. Try another font.', true); return; }
      var svg = glyphSVG(font, ch);
      if (!svg) { msg('“' + ch + '” has no outline to build. Try a letter or number.', true); return; }
      var safe = /[A-Za-z0-9]/.test(ch) ? ch : 'symbol';
      if (!feed(svg, safe + ' - ' + f.name + '.svg')) { msg('Your browser couldn’t load the letter. Try another up-to-date browser.', true); return; }
      msg('');
      track('bx_letter_select', { letter: ch, font: f.name });
    }).catch(function () {
      if (ticket === state.busy) msg('Couldn’t load that font. Check your connection and try again.', true);
    });
  }

  function pick(f) {
    state.font = f;
    markChip(f.id);
    apply();
  }

  function initLetters() {
    var input = $('bxl-char');
    if (!input || !$('bx-file')) return;
    buildChips();
    var t = null;
    input.addEventListener('input', function () {
      var chars = Array.from(input.value.trim());
      if (chars.length > 1) input.value = chars[chars.length - 1]; // typing over keeps the newest character
      renderChips();
      clearTimeout(t);
      if (state.font) t = setTimeout(apply, 250);
    });
    input.addEventListener('focus', function () { input.select(); });
    // load chip preview faces once the step is near the viewport
    var step = input.closest('.bt-step') || input;
    var loadAll = function () { FONTS.forEach(loadFace); };
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (es) {
        if (es.some(function (e) { return e.isIntersecting; })) { io.disconnect(); loadAll(); }
      }, { rootMargin: '300px' });
      io.observe(step);
    } else loadAll();
    // the upload and sample buttons leave letter mode
    ['bx-sample', 'bx-drop'].forEach(function (id) {
      var el = $(id);
      if (el) el.addEventListener('click', function () { state.font = null; state.busy++; markChip(null); msg(''); });
    });
  }

  /* ---------------- true-scale grid background ---------------- */
  var IN = 25.4;
  function initGrid() {
    var prev = $('bx-preview');
    if (!prev) return;
    var raf = 0;
    function paint() {
      raf = 0;
      var svg = prev.querySelector('svg');
      if (!svg) { prev.style.backgroundSize = ''; prev.style.backgroundPosition = ''; return; }
      // the preview's white sheet (first rect) is not part of the cut file; let the grid show through
      var sheet = svg.firstElementChild;
      if (sheet && sheet.tagName.toLowerCase() === 'rect' && /^(white|#fff(fff)?)$/i.test(sheet.getAttribute('fill') || '')) {
        sheet.setAttribute('fill', 'none');
      }
      // fill every piece white so it reads as paper on the mat (preview only; the download is untouched).
      // Faces are compound paths (outline + holes), so evenodd keeps the holes see-through.
      svg.querySelectorAll(':scope > g[id]').forEach(function (g) {
        if (g.id === 'labels') return;
        var cut = g.querySelector(':scope > path');
        if (cut && cut.getAttribute('fill') !== '#fff') {
          cut.setAttribute('fill', '#fff');
          cut.setAttribute('fill-rule', 'evenodd');
          g.insertBefore(cut, g.firstChild); // keep the fill under the perforations
        }
      });
      var vb = (svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
      var r = svg.getBoundingClientRect(), pr = prev.getBoundingClientRect();
      if (!vb[2] || !r.width) return;
      var pxPerMm = r.width / vb[2];
      var inches = $('bx-unitIn') ? $('bx-unitIn').getAttribute('aria-pressed') === 'true' : true;
      var cell = (inches ? IN : 10) * pxPerMm;
      while (cell < 14) cell *= 2;
      var ox = r.left - pr.left - prev.clientLeft + prev.scrollLeft;
      var oy = r.top - pr.top - prev.clientTop + prev.scrollTop;
      prev.style.backgroundSize = cell + 'px ' + cell + 'px';
      prev.style.backgroundPosition = ox + 'px ' + oy + 'px';
    }
    function queue() { if (!raf) raf = requestAnimationFrame(paint); }
    new MutationObserver(queue).observe(prev, { childList: true });
    if ('ResizeObserver' in window) new ResizeObserver(queue).observe(prev);
    window.addEventListener('resize', queue);
    ['bx-unitIn', 'bx-unitMm'].forEach(function (id) { var b = $(id); if (b) b.addEventListener('click', function () { setTimeout(queue, 0); }); });
    queue();
  }

  function init() { initLetters(); initGrid(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // exposed for testing
  window.BXLetters = { glyphSVG: glyphSVG, FONTS: FONTS };
})();